'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { RefObject, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { MovableModal } from '@flipster/features/src/shared/movable-modal';
import { SymbolContext } from '@flipster/features/src/shared/symbol/bunja';
import { adjustLeverageActionBunja } from '@flipster/features/src/shared/trade-actions/adjust-leverage/state';
import { ModalErrorBoundary } from '@flipster/features/src/utils/modal-error-boundary';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlayItem } from '@flipster/overlay';
import type { OpenPosition } from '@flipster/r230522/normal-sub/tables/private/positions';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { Providers } from '@flipster/react/providers/providers';
import { V2Position } from '@flipster/socket/r230522/position';
import Num from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { PositionEvent, PositionType } from '@flipster/utils/segment/events/position';

import { DefaultLeverageMarginModeContent } from '@web/app/[locale]/(web)/trade/_misc/order-form/perpetual/modals/default-leverage-margin-mode-modal';

import { useOpenConfirmDialog } from './confirm-dialog';
import { AdjustLeverageModalBody } from './modal-body';
import { AdjustLeverageDefaultsContext, AdjustLeverageModalContext, adjustLeverageModalBunja } from './state';

type Step = 'main' | 'defaultLeverageMarginMode';

interface PositionProps {
  type: 'position';
  from: 'orderform' | 'open-positions';
  position: V2Position | OpenPosition;
}

interface FormProps {
  type: 'form';
  from: 'orderform' | 'open-positions';
  symbol: string;
  defaultLeverage: number;
  /** confirm 시 선택한 leverage를 전달받는다. */
  onConfirmLeverage: (leverage: number) => void;
}

type Props = PositionProps | FormProps;

interface ContentRef {
  getLeverage: () => number;
}

const getPositionSide = (position: V2Position | OpenPosition) =>
  'side' in position ? position.side : Num(position.position).isGreaterThanOrEqualTo(0) ? 'Long' : 'Short';

const AdjustLeverageModal = (props: Props) => {
  const position = props.type === 'position' ? props.position : null;
  const symbol = props.type === 'position' ? props.position.symbol : props.symbol;
  const defaults = props.type === 'form' ? { defaultLeverage: props.defaultLeverage } : null;
  const { close } = useOverlayItem();

  return (
    <Providers
      providers={
        <>
          <SymbolContext value={symbol} />
          <AdjustLeverageModalContext.Provider value={position} />
          {/* eslint-disable-next-line react-hooks/exhaustive-deps */}
          <AdjustLeverageDefaultsContext.Provider value={useMemo(() => defaults, [defaults?.defaultLeverage])} />
        </>
      }
    >
      <ModalErrorBoundary onError={close}>
        <AdjustLeverageModalImpl
          from={props.from}
          onConfirmLeverage={props.type === 'form' ? props.onConfirmLeverage : undefined}
        />
      </ModalErrorBoundary>
    </Providers>
  );
};

interface AdjustLeverageModalImplProps {
  from: 'orderform' | 'open-positions';
  onConfirmLeverage?: (leverage: number) => void;
}

const AdjustLeverageModalImpl = ({ from, onConfirmLeverage }: AdjustLeverageModalImplProps) => {
  const { isOpen, close, unmount } = useOverlayItem();
  const contentRef = useRef<ContentRef>(null);
  const [step, setStep] = useState<Step>('main');

  const { currentPositionAtom } = useBunja(adjustLeverageModalBunja);
  const position = useAtomValue(currentPositionAtom);

  useEffect(() => {
    if (isOpen) segment.track(PositionEvent.AdjustLeverageBottomSheetView);
  }, [isOpen]);

  const handleClose = () => {
    close();
    if (position) {
      segment.track(PositionEvent.AdjustLeverageXClick, {
        symbol: position.symbol,
        position: getPositionSide(position).toLowerCase() as PositionType,
        leverage: contentRef.current?.getLeverage() || 1,
      });
    }
  };

  return (
    <MovableModal.Root isOpen={isOpen} onExit={unmount} onClose={handleClose}>
      {step === 'main' && (
        <AdjustLeverageMainContent
          from={from}
          onConfirmLeverage={onConfirmLeverage}
          contentRef={contentRef}
          onClose={close}
          onOpenDefaultLeverageMarginMode={() => setStep('defaultLeverageMarginMode')}
        />
      )}
      {step === 'defaultLeverageMarginMode' && (
        <DefaultLeverageMarginModeContent onBack={() => setStep('main')} afterConfirm={close} />
      )}
    </MovableModal.Root>
  );
};

interface AdjustLeverageMainContentProps {
  from: 'orderform' | 'open-positions';
  onConfirmLeverage?: (leverage: number) => void;
  contentRef: RefObject<ContentRef | null>;
  onClose: () => void;
  onOpenDefaultLeverageMarginMode: () => void;
}

const AdjustLeverageMainContent = ({
  from,
  onConfirmLeverage,
  contentRef,
  onClose,
  onOpenDefaultLeverageMarginMode,
}: AdjustLeverageMainContentProps) => {
  const {
    symbol,
    leverageAtom,
    isValidAtom,
    currentPositionAtom,
    currentPendingPositionsAtom,
    pricePrecisionAtom,
    modeMaxLeverageAtom,
    tradeModeAtom,
  } = useBunja(adjustLeverageModalBunja);
  const leverage = useAtomValue(leverageAtom);
  const isValid = useAtomValue(isValidAtom);
  const position = useAtomValue(currentPositionAtom);
  const currentPendingPositions = useAtomValue(currentPendingPositionsAtom);
  const pricePrecision = useAtomValue(pricePrecisionAtom);
  const modeMaxLeverage = useAtomValue(modeMaxLeverageAtom);
  const tradeMode = useAtomValue(tradeModeAtom);
  const isOneWay = tradeMode === 'OneWay';
  const hasPendingPosition = currentPendingPositions.length > 0;

  const { adjustLeverage, isAdjustLeverageLoadingAtom } = useBunja(adjustLeverageActionBunja);
  const isAdjustLeverageLoading = useAtomValue(isAdjustLeverageLoadingAtom);
  const openConfirmDialog = useOpenConfirmDialog();

  useImperativeHandle(contentRef, () => ({ getLeverage: () => leverage }));

  const executeConfirm = async () => {
    // 갱신 대상 결정:
    //   - position이 있으면 해당 position의 slot에 API 호출 (oneway slot 0이면 백엔드가 pending도 함께 갱신)
    //   - 없으면 orderform + oneway + pending 시나리오: pending의 slot에 API 호출
    //   - 그 외(no position, no pending): form leverage만 callback으로 갱신
    const targetPosition = (() => {
      if (position) return { symbol: position.symbol, slot: position.slot };
      if (isOneWay && from === 'orderform' && hasPendingPosition) {
        const p = currentPendingPositions[0];
        return { symbol: p.symbol, slot: p.slot };
      }
      return null;
    })();

    try {
      if (targetPosition) {
        await adjustLeverage(targetPosition.symbol, targetPosition.slot, leverage);
      } else {
        onConfirmLeverage?.(leverage);
      }
    } catch {
      // Error handled (snackbar) in adjustLeverageActionBunja
      return;
    }

    snackbar.success({ message: <FormattedMessage id='Position_leverage.r250401.Leverage_updated_toast' /> });
    onClose();
    segment.track(PositionEvent.AdjustLeverageConfirmClick, {
      symbol: targetPosition?.symbol ?? symbol,
      adjust_leverage: leverage,
    });
  };

  const handleConfirm = async () => {
    try {
      if (isOneWay && (position || hasPendingPosition)) await openConfirmDialog();
      await executeConfirm();
    } catch {
      // dialog rejected
    }
  };

  return (
    <>
      <MovableModal.Header>
        <MovableModal.Title label={<FormattedMessage id='Position_leverage.r250401.Adjust_leverage_title' />} />
      </MovableModal.Header>
      <MovableModal.Content>
        <AdjustLeverageModalBody
          position={position}
          symbol={position?.symbol ?? symbol}
          pricePrecision={pricePrecision}
          modeMaxLeverage={modeMaxLeverage}
          from={from}
          onOpenDefaultLeverageMarginMode={onOpenDefaultLeverageMarginMode}
        />
      </MovableModal.Content>
      <MovableModal.Footer>
        <MovableModal.Button
          hierarchy='secondary'
          color='neutral'
          size='large'
          label={<FormattedMessage id='Common.r250401.Cancel' />}
          onClick={() => {
            onClose();
            if (position) {
              segment.track(PositionEvent.AdjustLeverageCancelClick, {
                symbol: position.symbol,
                position: getPositionSide(position).toLowerCase() as PositionType,
                leverage,
              });
            }
          }}
        />
        <MovableModal.Button
          hierarchy='primary'
          color='accent'
          size='large'
          label={<FormattedMessage id='Common.r250401.Confirm' />}
          isLoading={position || hasPendingPosition ? isAdjustLeverageLoading : false}
          disabled={!isValid}
          onClick={handleConfirm}
        />
      </MovableModal.Footer>
    </>
  );
};

export default AdjustLeverageModal;
